import React from 'react'
import { IFormProps } from '../../lib/types'
import { FormikProps, Formik } from 'formik'
import { Input, GoogleReCaptcha } from '../../lib/components'
import { Link } from 'react-router-dom'
import { GLogin } from '../login/google-login-container'
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator'

type SignupV2InitialValues = {
  name: string
  email: string
  password: string
  passwordConfirmation: string
  referralCode: string
  termsAccepted: boolean
}

type SignupV2PresenterProps = Omit<IFormProps, 'googleLoginHandler'> & {
  isReferralCodeLocked?: boolean
  googleLoginHandler?: (res: object, referralCode?: string) => void
}

const SignupV2Presenter = ({
  initialValues,
  handleFormSubmit,
  validationSchema,
  reRef,
  googleLoginHandler,
  isReferralCodeLocked = false,
}: SignupV2PresenterProps) => {
  return (
    <Formik
      initialValues={initialValues as unknown as SignupV2InitialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {(formik) => {
        const {
          values,
          touched,
          errors,
          dirty,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
        }: FormikProps<SignupV2InitialValues> = formik
        const canSubmit =
          values.name?.trim() &&
          values.email?.trim() &&
          values.password &&
          values.passwordConfirmation &&
          values.termsAccepted
        const buttonClass = isSubmitting
          ? 'sign-up-page__btn-primary sign-up-page__btn-primary--loading'
          : canSubmit
          ? 'sign-up-page__btn-primary sign-up-page__btn-primary--enabled'
          : 'sign-up-page__btn-primary sign-up-page__btn-primary--disabled'

        return (
          <>
            <h2 className='sign-up-page__form-title'>Create your account</h2>
            <form className='sign-up-page__form' onSubmit={handleSubmit} noValidate>
              <Input
                labelText='Full Name'
                name='name'
                placeholder='Enter your full name'
                type='text'
                required
                data-testid='full name-input'
                value={values.name}
                handleChange={handleChange(initialValues.name)}
                dirty={dirty}
                errors={{ name: !!errors && errors.name ? errors.name : null }}
                touched={{ name: !!touched && !!touched.name }}
              />
              <p className='sign-up-page__field-helper' data-testid='full-name-helper'>
                Please enter your First and Last name as written in your official documents.
              </p>
              <Input
                labelText='Email'
                name='email'
                placeholder='Enter your email address'
                type='text'
                required
                data-testid='email-input'
                value={values.email}
                handleChange={handleChange(initialValues.email)}
                dirty={dirty}
                errors={{ email: !!errors && errors.email ? errors.email : null }}
                touched={{ email: !!touched && !!touched.email }}
              />
              <div className='sign-up-page__input-wrap'>
                <Input
                  labelText='Set a password'
                  name='password'
                  placeholder='Create a strong password'
                  required
                  type='password'
                  data-testid='password-input'
                  value={values.password}
                  handleChange={handleChange(initialValues.password)}
                  dirty={dirty}
                  errors={{
                    password: !!errors && errors.password ? errors.password : null,
                  }}
                  touched={{ password: !!touched && !!touched.password }}
                />
                <PasswordStrengthIndicator password={values.password} />
              </div>
              <div className='sign-up-page__input-wrap'>
                <Input
                  labelText='Repeat password'
                  name='passwordConfirmation'
                  placeholder='Re-enter your password'
                  required
                  type='password'
                  data-testid='repeat password-input'
                  value={values.passwordConfirmation}
                  handleChange={handleChange(initialValues.passwordConfirmation)}
                  dirty={dirty}
                  errors={{
                    passwordConfirmation:
                      !!errors && errors.passwordConfirmation ? errors.passwordConfirmation : null,
                  }}
                  touched={{
                    passwordConfirmation: !!touched && !!touched.passwordConfirmation,
                  }}
                />
              </div>
              <div className='sign-up-page__input-wrap'>
                <div className='sign-up-page__label-row'>
                  <span className='sign-up-page__label input-label'>Referral Code</span>
                  <span className='sign-up-page__optional'>(Optional)</span>
                </div>
                <input
                  type='text'
                  name='referralCode'
                  className={`sign-up-page__input${
                    isReferralCodeLocked ? ' sign-up-page__input--locked' : ''
                  }`}
                  placeholder='Enter code'
                  value={values.referralCode}
                  onChange={(e) => setFieldValue('referralCode', e.target.value)}
                  onBlur={() => setFieldTouched('referralCode', true)}
                  readOnly={isReferralCodeLocked}
                  aria-readonly={isReferralCodeLocked}
                  data-testid='referral-code-input'
                />
              </div>

              <div className='sign-up-page__terms'>
                <label className='sign-up-page__checkbox-label'>
                  <input
                    type='checkbox'
                    name='termsAccepted'
                    checked={!!values.termsAccepted}
                    onChange={(e) => setFieldValue('termsAccepted', e.target.checked)}
                    onBlur={() => setFieldTouched('termsAccepted', true)}
                    className='sign-up-page__checkbox'
                    data-testid='terms-checkbox'
                  />
                  <span className='sign-up-page__terms-text'>
                    I agree to the{' '}
                    <Link
                      to='/terms_and_conditions'
                      target='_blank'
                      className='sign-up-page__terms-link'
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to='/privacy_policy' target='_blank' className='sign-up-page__terms-link'>
                      Privacy Policy
                    </Link>
                  </span>
                </label>
                {touched.termsAccepted && errors.termsAccepted && (
                  <div className='sign-up-page__error'>{errors.termsAccepted}</div>
                )}
              </div>

              <GoogleReCaptcha
                handleChange={(value) => setFieldValue('reCaptchaToken', value)}
                reCaptchaRef={reRef}
                size='invisible'
              />

              <button
                type='submit'
                className={buttonClass}
                disabled={isSubmitting}
                data-testid='sign-up-button'
              >
                {isSubmitting ? (
                  <>
                    <span className='sign-up-page__spinner' aria-hidden />
                    Creating...
                  </>
                ) : (
                  'Create account'
                )}
              </button>

              {googleLoginHandler && (
                <>
                  <div className='sign-up-page__divider'>
                    <span className='sign-up-page__divider-text'>Or continue with</span>
                  </div>
                  <div className='sign-up-page__google-wrap'>
                    <GLogin
                      buttonText='Sign up with Google'
                      onFailure={(res) => console.log('Login Failed ', res)}
                      onSuccess={(res) => googleLoginHandler?.(res, values.referralCode)}
                    />
                  </div>
                </>
              )}

              <p className='sign-up-page__sign-in-prompt'>
                Already have an account?{' '}
                <Link to='/login' className='sign-up-page__sign-in-link' data-testid='sign-in-link'>
                  Login
                </Link>
              </p>
            </form>
          </>
        )
      }}
    </Formik>
  )
}

export { SignupV2Presenter }
